import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useAuthStore } from '@/store/authStore'
import {
  useDashboardProfile,
  useDashboardLearning,
  useDashboardAiq,
} from '@/hooks/useDashboard'
import {
  LayoutDashboard,
  User as UserIcon,
  Building,
  Mail,
  TrendingUp,
  BookOpen,
  CheckCircle2,
  Clock,
  ArrowRight,
  Brain,
  Loader2,
} from 'lucide-react'

// ============================================================================
// Loading Skeleton Components
// ============================================================================

function CardSkeleton() {
  return (
    <div className="p-6 bg-white rounded-xl shadow-sm animate-pulse">
      <div className="mb-4 w-1/3 h-4 bg-gray-200 rounded" />
      <div className="space-y-3">
        <div className="w-full h-3 bg-gray-200 rounded" />
        <div className="w-2/3 h-3 bg-gray-200 rounded" />
        <div className="w-1/2 h-3 bg-gray-200 rounded" />
      </div>
    </div>
  )
}

// ============================================================================
// Sub Components
// ============================================================================

function UserProfileCard() {
  const { user } = useAuthStore()
  const { data: profile, isLoading } = useDashboardProfile()
  const { t } = useTranslation('learner')

  const initials = user?.fullName?.slice(0, 2) || user?.email?.slice(0, 2) || '??'

  const dept = profile?.departmentName || t('dashboard.noDept')
  const position = profile?.position || ''
  const employeeId = profile?.employeeId || '-'
  const departmentAndGrade = [dept, position].filter(Boolean).join(' · ') || t('dashboard.noDeptPosition')

  if (isLoading) {
    return <CardSkeleton />
  }

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm">
      <div className="flex gap-4 items-start">
        {/* Avatar */}
        <div className="flex flex-shrink-0 justify-center items-center w-16 h-16 text-xl font-bold text-white rounded-full bg-primary-600">
          {initials}
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0">
          <h3 className="mb-1 text-xl font-bold text-gray-900">{user?.fullName || t('dashboard.user')}</h3>
          <div className="space-y-1.5">
            <div className="flex gap-2 items-center text-sm text-gray-600">
              <Building className="w-4 h-4 shrink-0" />
              <span className="truncate">{departmentAndGrade}</span>
            </div>
            <div className="flex gap-2 items-center text-sm text-gray-600">
              <UserIcon className="w-4 h-4 shrink-0" />
              <span className="truncate">{employeeId}</span>
            </div>
            <div className="flex gap-2 items-center text-sm text-gray-600">
              <Mail className="w-4 h-4 shrink-0" />
              <span className="truncate">{user?.email || t('dashboard.noEmail')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LearningStatusCard() {
  const { stats, isLoading } = useDashboardLearning()
  const { t } = useTranslation('learner')
  const overallProgress = Math.min(100, Math.max(0, Math.round(stats.overallProgressPercent)))

  const formatRatio = (completed: number, total: number) =>
    total ? `${completed}/${total}` : `${completed}`

  if (isLoading) {
    return <CardSkeleton />
  }

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.learningStatus')}</h3>
        <TrendingUp className="w-5 h-5 text-green-500" />
      </div>

      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">{t('dashboard.overallProgress')}</span>
          <span className="text-sm font-bold text-primary-600">{overallProgress}%</span>
        </div>
        <div className="overflow-hidden h-2 bg-gray-200 rounded-full">
          <div
            className="h-full rounded-full transition-all duration-300 bg-primary-500"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <StatItem
          icon={<Clock className="w-4 h-4" />}
          label={t('dashboard.inProgressCourses')}
          value={t('dashboard.countUnit', { count: stats.inProgressCurriculums })}
          color="text-purple-600"
        />
        <StatItem
          icon={<TrendingUp className="w-4 h-4" />}
          label={t('dashboard.completedCourses')}
          value={formatRatio(stats.completedCurriculums, stats.totalCurriculums)}
          color="text-orange-600"
        />
        <StatItem
          icon={<BookOpen className="w-4 h-4" />}
          label={t('dashboard.completedModules')}
          value={formatRatio(stats.completedModules, stats.totalModules)}
          color="text-blue-600"
        />
        <StatItem
          icon={<CheckCircle2 className="w-4 h-4" />}
          label={t('dashboard.completedLessons')}
          value={formatRatio(stats.completedLessons, stats.totalLessons)}
          color="text-green-600"
        />
      </div>
    </div>
  )
}

function StatItem({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode
  label: string
  value: string
  color: string
}) {
  return (
    <div className="flex gap-2 items-start">
      <div className={`${color} mt-0.5`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-gray-900 truncate">{value}</p>
      </div>
    </div>
  )
}

function ClassroomQuickAccessCard() {
  const { quickAccess, isLoading } = useDashboardLearning()
  const { t } = useTranslation('learner')

  if (isLoading) {
    return <CardSkeleton />
  }

  // 진행 중인 코스이 없는 경우
  if (!quickAccess) {
    return (
      <div className="flex flex-col justify-between p-6 h-full bg-white rounded-xl shadow-sm">
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {t('dashboard.classroom')}
            </span>
            <BookOpen className="w-4 h-4 text-primary-600" />
          </div>
          <p className="text-lg font-semibold text-gray-900">{t('dashboard.noActiveCourse')}</p>
          <p className="text-sm text-gray-500">
            {t('dashboard.noActiveCourseDesc')}
          </p>
        </div>
        <div className="mt-4">
          <Link
            to="/my/courses"
            className="inline-flex gap-2 items-center text-sm font-medium text-primary-600"
          >
            {t('dashboard.goToClassroom')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <Link to={`/learn/${quickAccess.classProductId}/${quickAccess.contentId}`} className="block h-full group">
      <div className="overflow-hidden h-full bg-white rounded-xl shadow-sm transition-shadow hover:shadow-md">
        {/* Thumbnail Area */}
        <div className={`relative h-40 w-full overflow-hidden ${quickAccess.pendingModuleEndQuiz
            ? 'bg-gradient-to-br from-amber-500 via-amber-400 to-orange-400'
            : 'bg-gradient-to-r from-primary-500/20 via-primary-500/10 to-primary-500/5'
          }`}>
          {quickAccess.thumbnailUrl && (
            <img
              src={quickAccess.thumbnailUrl}
              alt={quickAccess.courseTitle}
              className="object-cover w-full h-full"
            />
          )}
          <div className="absolute inset-0 transition-colors bg-black/10 group-hover:bg-black/5" />
          <div className="flex absolute right-3 bottom-3 left-3 gap-2 items-center">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${quickAccess.pendingModuleEndQuiz
                ? 'bg-white text-amber-700'
                : 'bg-white/90 text-gray-800'
              }`}>
              {quickAccess.pendingModuleEndQuiz ? t('dashboard.moduleQuiz') : t('dashboard.classroom')}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="mb-3 text-lg font-bold text-gray-900 line-clamp-1"><ContentMetadataText text={quickAccess.courseTitle} /></h3>

          <div className="space-y-1">
            {quickAccess.moduleTitle && (
              <p className="text-sm font-medium text-gray-500"><ContentMetadataText text={quickAccess.moduleTitle} /></p>
            )}
            {quickAccess.pendingModuleEndQuiz ? (
              <p className="text-base font-semibold text-amber-600">{t('dashboard.moduleEndQuiz')}</p>
            ) : quickAccess.lessonTitle ? (
              <p className="text-base font-semibold text-gray-800"><ContentMetadataText text={quickAccess.lessonTitle} /></p>
            ) : null}
          </div>

          <div className="flex items-center mt-4 text-sm font-medium text-primary-600">
            <span>
              {quickAccess.pendingModuleEndQuiz
                ? t('dashboard.takeQuiz')
                : quickAccess.hasStarted
                  ? t('dashboard.continueLearn')
                  : t('dashboard.startLearn')}
            </span>
            <ArrowRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  )
}

function AIQStatusCard() {
  const { courses } = useDashboardLearning()
  const firstCourse = courses[0]
  const { data: aiq, isLoading } = useDashboardAiq(firstCourse?.productId, firstCourse?.classProductId)
  const { t } = useTranslation('learner')

  if (isLoading) {
    return <CardSkeleton />
  }

  // AIQ 미설정 시 카드 숨김
  if (aiq && !aiq.preAiqEnabled && !aiq.postAiqEnabled) {
    return null
  }

  const renderStatus = (label: string, status?: string, date?: string | null) => {
    const displayStatus = status || 'not_started'
    return (
      <div className="flex justify-between items-center py-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex gap-2 items-center">
          {displayStatus === 'passed' || displayStatus === 'completed' ? (
            <span className="text-xs text-gray-500">
              {t('dashboard.completedDate')} {date ? new Date(date).toLocaleDateString() : ''}
            </span>
          ) : displayStatus === 'in_progress' ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-600">
              {t('dashboard.inProgress')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              {t('dashboard.scheduled')}
            </span>
          )}
        </div>
      </div>
    )
  }

  // AIQ 데이터가 없으면 기본 상태 표시
  const preStatus = aiq?.preTest?.status || 'not_started'
  const postStatus = aiq?.postTest?.status || 'not_started'
  const preCompletedAt = aiq?.preTest?.completedAt
  const postCompletedAt = aiq?.postTest?.completedAt

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm">
      <div className="flex gap-2 items-center mb-4">
        <Brain className="w-5 h-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900">AIQ</h3>
      </div>

      <div className="space-y-2 divide-y divide-gray-100">
        {renderStatus('Pre-AIQ Test', preStatus, preCompletedAt)}
        {renderStatus('Post-AIQ Test', postStatus, postCompletedAt)}
        {renderStatus(t('dashboard.aiqReport'), preStatus === 'completed' && postStatus === 'completed' ? 'completed' : 'not_started')}
      </div>

      <Link
        to="/my/aiq"
        className="block px-4 py-2 mt-4 w-full text-sm font-medium text-center text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
      >
        {t('dashboard.viewAiqTest')}
      </Link>
    </div>
  )
}

// ============================================================================
// Main Page Component
// ============================================================================

export default function MyDashboard() {
  const { t } = useTranslation('learner')

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex gap-3 items-center mb-2">
          <LayoutDashboard className="w-8 h-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        </div>
        <p className="text-gray-500">{t('dashboard.subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Row 1: Profile + Learning Status */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-6">
          <UserProfileCard />
          <LearningStatusCard />
        </div>

        {/* Row 2: Classroom + AIQ */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-6">
          <ClassroomQuickAccessCard />
          <AIQStatusCard />
        </div>
      </div>
    </div>
  )
}
